import { useEffect, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import axios, { pluckError } from 'src/utilities/http-client'
import { useAppSelector } from 'src/store/hooks'
import {
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  RecertificationCycleTemplateCategory,
  RecertificationCycleTemplateCourse,
} from 'src/v3/core/types'

type Props = {
  templateCategoryID?: string
  course?: Partial<RecertificationCycleTemplateCourse>
  categories: RecertificationCycleTemplateCategory[]
  existingCourses: RecertificationCycleTemplateCourse[]
  open: boolean
  onClose: () => void
  onSaved: () => void
}

export default function TemplateCourseForm({
  templateCategoryID,
  course,
  categories,
  existingCourses,
  open,
  onClose,
  onSaved,
}: Props) {
  let { showError, NotificationComponent } = useNotifications()
  let [saving, setSaving] = useState(false)

  let allCourses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  useAutoFetch(coursesStatus, fetchCourses)

  let existingAssignments = existingCourses.filter(
    (ec) => ec.courseID === course?.courseID,
  )

  let form = useForm({
    defaultValues: {
      courseID: course?.courseID || '',
      categories:
        existingAssignments.length > 0
          ? existingAssignments.map((ec) => ({
              id: ec.id,
              category: ec.templateCategoryID,
              hours: ec.hours.toString(),
            }))
          : [
              {
                id: undefined as string | undefined,
                category:
                  course?.templateCategoryID ||
                  templateCategoryID ||
                  'unselected',
                hours: '',
              },
            ],
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let ids = value.categories.map((c) => c.id)
      Promise.all([
        ...existingAssignments
          .filter((ec) => !ids.includes(ec.id))
          .map((ec) =>
            axios.delete(
              `/api/manage/recertification-cycle/template/course/${ec.id}`,
            ),
          ),
        ...value.categories.map((cat) =>
          !cat.id
            ? axios.post(`/api/manage/recertification-cycle/template/course`, {
                courseID: value.courseID,
                templateCategoryID: cat.category,
                hours: parseFloat(cat.hours),
              })
            : axios.put(`/api/manage/recertification-cycle/template/course`, {
                id: cat.id,
                courseID: value.courseID,
                templateCategoryID: cat.category,
                hours: parseFloat(cat.hours),
              }),
        ),
      ])
        .then(() => {
          onSaved()
          onClose()
          formApi.reset()
        })
        .catch((err) => showError?.(pluckError(err, 'Error saving course')))
        .finally(() => setSaving(false))
    },
  })

  useEffect(() => {
    if (course?.courseID) {
      form.setFieldValue('courseID', course.courseID)
      form.setFieldValue(
        'categories',
        existingAssignments.length > 0
          ? existingAssignments.map((ec) => ({
              id: ec.id,
              category: ec.templateCategoryID,
              hours: ec.hours.toString(),
            }))
          : [
              {
                id: undefined,
                category:
                  course.templateCategoryID ||
                  templateCategoryID ||
                  'unselected',
                hours: course.hours?.toString() || '',
              },
            ],
      )
    }
  }, [course?.courseID])

  let handleClose = () => {
    form.reset()
    onClose()
  }

  let assignedCourseIDs = existingCourses
    .filter((ec) => ec.courseID !== course?.courseID)
    .map((ec) => ec.courseID)

  let availableCourses = (allCourses ?? []).filter(
    (c) => !assignedCourseIDs.includes(c.id),
  )

  return (
    <Dialog open={open} onClose={handleClose}>
      <Paper sx={{ width: 600, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Add Course
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography>1. Select Course</Typography>
            <form.Field
              name="courseID"
              validators={{
                onChange: ({ value }) =>
                  !value ? 'Course is a required field' : undefined,
              }}
              listeners={{
                onChange: ({ value }) => {
                  if (form.state.values.categories.length === 1) {
                    let selectedCourse = (allCourses ?? []).find(
                      (c) => c.id === value,
                    )
                    form.setFieldValue(
                      'categories[0].hours',
                      selectedCourse?.length?.toString() || '',
                    )
                    if (
                      form
                        .getFieldValue('categories')
                        .every((c) => c.category === 'unselected')
                    ) {
                      let sectionName = selectedCourse?.section
                      let match = categories.find((c) => c.name === sectionName)
                      if (match && sectionName) {
                        form.setFieldValue('categories[0].category', match.id)
                      }
                    }
                  }
                },
              }}
              children={(field) => (
                <Autocomplete
                  value={
                    (allCourses ?? []).find(
                      (c) => c.id === field.state.value,
                    ) ?? null
                  }
                  onChange={(_, value) => field.handleChange(value?.id || '')}
                  options={availableCourses}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    // eslint-disable-next-line fp/no-rest-parameters
                    let { key, ...optionProps } = props
                    return (
                      <Box
                        key={key}
                        component="li"
                        {...optionProps}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                      >
                        <Typography sx={{ flex: '1 1 400px' }}>
                          {option.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${option.length} Hour${option.length === 1 ? '' : 's'}`}
                        />
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'background.paper' }}
                      label="Course"
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.courseID}
              children={(courseID) => {
                let hours = (allCourses ?? []).find(
                  (c) => c.id === courseID,
                )?.length
                return (
                  <form.Field
                    name="categories"
                    mode="array"
                    validators={{
                      onSubmit: ({ value }) =>
                        value.reduce(
                          (sum, c) => sum + parseFloat(c.hours || '0'),
                          0,
                        ) !== hours
                          ? `All hours must be assigned & all category hours must add up to ${hours} hours.`
                          : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            2. Assign {hours} Hour{hours === 1 ? '' : 's'}
                          </Typography>
                          <Button
                            color="secondary"
                            sx={{ mb: -0.25 }}
                            size="small"
                            onClick={() =>
                              field.pushValue({
                                id: undefined,
                                category: 'unselected',
                                hours: '',
                              })
                            }
                          >
                            Add Category
                          </Button>
                          <Button
                            color="error"
                            sx={{ mb: -0.25 }}
                            size="small"
                            onClick={() =>
                              field.removeValue(field.state.value.length - 1)
                            }
                            disabled={field.state.value.length === 1}
                          >
                            Remove Category
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {field.state.value.map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                              <form.Field
                                name={`categories[${i}].category`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    value === 'unselected'
                                      ? 'Category is a required field'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <FormControl
                                    fullWidth
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                  >
                                    <InputLabel
                                      id={`course-category-label-${i}`}
                                    >
                                      Category
                                    </InputLabel>
                                    <Select
                                      labelId={`course-category-label-${i}`}
                                      value={subField.state.value}
                                      label="Category"
                                      onChange={(e) =>
                                        subField.handleChange(e.target.value)
                                      }
                                      sx={{ bgcolor: 'background.paper' }}
                                    >
                                      <MenuItem value="unselected">
                                        Unselected
                                      </MenuItem>
                                      {categories
                                        .filter(
                                          (cat) =>
                                            cat.id === subField.state.value ||
                                            !field.state.value
                                              .map((c) => c.category)
                                              .includes(cat.id),
                                        )
                                        .toSorted((a, b) =>
                                          `${a.name}${a.subcategory || ''}`.localeCompare(
                                            `${b.name}${b.subcategory || ''}`,
                                          ),
                                        )
                                        .map((cat) => (
                                          <MenuItem key={cat.id} value={cat.id}>
                                            {cat.name}
                                            {cat.subcategory &&
                                              ` (${cat.subcategory})`}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {subField.state.meta.errors.length > 0 && (
                                      <FormHelperText>
                                        {subField.state.meta.errors.join(', ')}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                              <form.Field
                                name={`categories[${i}].hours`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    !parseFloat(value)
                                      ? 'Hours is a required field & must be greater than 0'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <TextField
                                    sx={{ bgcolor: 'background.paper' }}
                                    fullWidth
                                    variant="outlined"
                                    value={subField.state.value}
                                    label="Hours"
                                    name={subField.name}
                                    onChange={(e) => {
                                      subField.handleChange(
                                        e.target.value.replace(/[^\d.]/g, ''),
                                      )
                                      field.validate('change')
                                    }}
                                    onBlur={subField.handleBlur}
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                    helperText={subField.state.meta.errors.join(
                                      ', ',
                                    )}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                          {field.state.meta.errors.length > 0 && (
                            <FormHelperText sx={{ color: 'error.main' }}>
                              {field.state.meta.errors.join(', ')}
                            </FormHelperText>
                          )}
                        </Box>
                      </>
                    )}
                  </form.Field>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleClose}
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
